/**
 * Courier Performance — main page with Forward/Reverse tabs and delivery sub-type filter.
 * Data comes from the backend D2C Courier Performance endpoint (clubs CT freightsnapshot by
 * proship service type, merchant-scoped). Uses RTK Query so revisiting a tab/range served from cache.
 */
import React, {useState, useEffect} from 'react'
import {subDays} from 'date-fns'
import {Box, Tabs, Tab, FormControl, InputLabel, MenuItem, Select, Typography} from '@mui/material'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import {apiConstants} from '../../../common/constants'
import {useGenericPostRequestQuery} from '../../../redux/commonRTK'
import {getYYYYMMDD} from '../../../helpers/UtilityHelper'
import DashboardDateFilter, {DateRangeCaption} from '../../../common/DashboardDateFilter'
import Loader from '../../../common/Loader'
import ForwardPerformance from './ForwardPerformance'
import ReversePerformance from './ReversePerformance'
import {FORWARD_SUB_TYPES, REVERSE_SUB_TYPES, SECTION_TO_BUCKET, resolveMerchantId} from './performanceUtils'
import {FONT, SIZE, WEIGHT} from '../../../styles/foundation/typography'
import {NAVY, BORDER, SUBTLE, WHITE} from '../../../styles/foundation/colors'
import {SHADOW} from '../../../styles/foundation/effects'
import {tabBarSx} from '../../../styles/patterns'

const selectSx = {
  fontFamily: FONT, fontSize: SIZE.xs,
  '& .MuiOutlinedInput-root': {borderRadius: '8px', fontSize: SIZE.xs},
  '& .MuiSelect-select': {py: 0.8},
}

// FE service-type buckets → backend `page` values.
const toBackendPage = (bucket) => {
  if (bucket === 'SAME_CITY_NDD') return 'SC-NDD'
  if (bucket === 'INTERCITY_NDD') return 'IC-NDD'
  return bucket || 'ALL' // ALL / SURFACE / AIR / SDD
}

export default function CourierPerformance({section}) {
  // The route segment (Surface/Air/SDD/SC-NDD/IC-NDD) sets the default service type.
  const routeBucket = SECTION_TO_BUCKET[section] || 'ALL'
  const [tab, setTab] = useState(0)
  const [subType, setSubType] = useState(routeBucket)
  const [dateRange, setDateRange] = useState([{
    endDate: subDays(new Date(), 1), startDate: subDays(new Date(), 30), key: 'selection',
  }])

  // When the route's service type changes (navigating SDD ↔ Air ↔ …), reset to
  // the Forward tab and default the filter to that service type.
  useEffect(() => { setTab(0); setSubType(routeBucket) }, [routeBucket])

  const merchantId = resolveMerchantId()
  // RTK Query caches per serialized args (60-min TTL) — switching back to a previously
  // loaded tab/range serves from cache instead of re-hitting the API.
  const request = {
    url: apiConstants.D2C_COURIER_PERFORMANCE,
    type: 'POST',
    data: {
      merchantId,
      fromDate: getYYYYMMDD(dateRange[0].startDate),
      toDate: getYYYYMMDD(dateRange[0].endDate),
      page: tab === 0 ? toBackendPage(subType) : 'ALL',
      direction: tab === 0 ? 'FORWARD' : 'REVERSE',
    },
  }
  // No skip — backend resolves the merchant from the JWT when the FE sends none (admin users
  // often have no merchant in context). data is the response body (array of rows).
  const {data, isFetching} = useGenericPostRequestQuery(request)
  const rows = Array.isArray(data) ? data : (Array.isArray(data?.data) ? data.data : [])

  const subTypes = tab === 0 ? FORWARD_SUB_TYPES : REVERSE_SUB_TYPES
  const subTypeLabel = (subTypes.find((s) => s.value === subType) || {}).label || 'this service type'
  // The active tab has no real data for the selected service type / range.
  const activeEmpty = !isFetching && rows.length === 0

  return (
    <>
      {isFetching && <Loader />}

      <DateRangeCaption dateRange={dateRange} sx={{mb: 2}} />

      {/* Filter bar */}
      <Box sx={{
        bgcolor: WHITE, borderRadius: '10px', boxShadow: SHADOW.card,
        border: `1px solid ${BORDER}`, px: 2.5, py: 1.5, mb: 2.5,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5,
      }}>
        <Tabs value={tab} onChange={(_, v) => { setTab(v); setSubType(v === 0 ? routeBucket : 'ALL') }} sx={{...tabBarSx, mb: 0, borderBottom: 'none'}}>
          <Tab label='Forward Performance' />
          <Tab label='Reverse Performance' />
        </Tabs>
        <Box sx={{display: 'flex', alignItems: 'center', gap: 1.5}}>
          <FormControl variant='outlined' size='small' sx={{minWidth: 160}}>
            <InputLabel sx={{fontFamily: FONT, fontSize: SIZE.xs}}>Delivery Type</InputLabel>
            <Select value={subType} onChange={(e) => setSubType(e.target.value)} label='Delivery Type' sx={selectSx}>
              {subTypes.map((t) => (
                <MenuItem key={t.value} value={t.value} sx={{fontFamily: FONT, fontSize: SIZE.xs}}>{t.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <DashboardDateFilter dateRange={dateRange} maxDate={subDays(new Date(), 1)} onChange={setDateRange} showPresets={false} />
        </Box>
      </Box>

      {/* Tab content — real data, or an honest empty state (no fake placeholders) */}
      {activeEmpty ? (
        <Box sx={{bgcolor: WHITE, borderRadius: '10px', boxShadow: SHADOW.card, border: `1px solid ${BORDER}`, p: 5, textAlign: 'center'}}>
          <InfoOutlinedIcon sx={{fontSize: 40, color: BORDER, mb: 1}} />
          <Typography sx={{fontFamily: FONT, fontSize: SIZE.sm, fontWeight: WEIGHT.bold, color: NAVY}}>
            No {subTypeLabel} performance data
          </Typography>
          <Typography sx={{fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE, mt: 0.5, maxWidth: 460, mx: 'auto'}}>
            No shipments were booked on a {subTypeLabel} courier for the selected date range. Try a wider range, or check a different service type.
          </Typography>
        </Box>
      ) : (
        <>
          {tab === 0 && <ForwardPerformance data={rows} />}
          {tab === 1 && <ReversePerformance data={rows} />}
        </>
      )}
    </>
  )
}
